import { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

/**
 * Monitor status type
 */
export type MonitorStatus = 'UP' | 'DOWN' | 'PENDING' | 'PAUSED';

/**
 * Check data structure for uptime visualization
 */
interface DayData {
  date: string;
  uptimePercentage: number;
  hasData: boolean;
}

/**
 * Monitor data for the row component
 */
export interface StatusPageMonitorData {
  id: string;
  displayName: string;
  status: MonitorStatus;
  uptimePercentage: number;
  avgResponseTime: number | null;
  lastCheckedAt: Date | string | null;
  uptimeHistory?: DayData[];
}

interface StatusPageMonitorRowProps {
  monitor: StatusPageMonitorData;
  days?: 30 | 90;
  primaryColor?: string;
  showDetails?: boolean;
}

/**
 * Get status configuration based on monitor status
 */
const statusConfig: Record<
  MonitorStatus,
  {
    label: string;
    color: string;
    backgroundColor: string;
    icon: React.ReactElement;
  }
> = {
  UP: {
    label: 'Operational',
    color: '#10B981',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,
  },
  DOWN: {
    label: 'Down',
    color: '#EF4444',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    icon: <ErrorIcon sx={{ fontSize: 18 }} />,
  },
  PENDING: {
    label: 'Unknown',
    color: '#6B7280',
    backgroundColor: 'rgba(107, 114, 128, 0.1)',
    icon: <HourglassEmptyIcon sx={{ fontSize: 18 }} />,
  },
  PAUSED: {
    label: 'Maintenance',
    color: '#F59E0B',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    icon: <PauseCircleIcon sx={{ fontSize: 18 }} />,
  },
};

/**
 * Get color based on uptime percentage
 */
function getUptimeColor(uptimePercentage: number, hasData: boolean, primaryColor?: string): string {
  if (!hasData) return '#E5E7EB';
  if (uptimePercentage === 100) return primaryColor || '#10B981';
  if (uptimePercentage >= 95) return '#34D399';
  if (uptimePercentage >= 80) return '#FBBF24';
  if (uptimePercentage >= 50) return '#F59E0B';
  return '#EF4444';
}

/**
 * Generate mock uptime history data for display
 */
function generateMockHistory(days: number, uptimePercentage: number): DayData[] {
  const history: DayData[] = [];
  const now = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);

    // Simulate some variance based on overall uptime
    const variance = Math.random() * 10 - 5;
    let dayUptime = Math.min(100, Math.max(0, uptimePercentage + variance));

    // Ensure we have some realistic variation
    if (Math.random() > 0.95 && uptimePercentage < 100) {
      dayUptime = Math.random() * 50;
    }

    history.push({
      date: date.toISOString().split('T')[0] ?? date.toISOString(),
      uptimePercentage: Math.round(dayUptime),
      hasData: true,
    });
  }

  return history;
}

/**
 * MiniUptimeBar component for displaying uptime history
 */
function MiniUptimeBar({
  history,
  days,
  primaryColor,
}: {
  history: DayData[];
  days: number;
  primaryColor?: string | undefined;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Reduce number of bars on mobile
  const displayDays = isMobile ? Math.min(days, 30) : days;
  const displayHistory = history.slice(-displayDays);

  return (
    <Box sx={{ display: 'flex', gap: '2px', height: 24, alignItems: 'flex-end' }}>
      {displayHistory.map((day, index) => (
        <Tooltip
          key={index}
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                {new Date(day.date).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </Typography>
              {day.hasData ? (
                <Typography variant="caption">Uptime: {day.uptimePercentage}%</Typography>
              ) : (
                <Typography variant="caption">No data</Typography>
              )}
            </Box>
          }
          arrow
          placement="top"
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 3,
              maxWidth: 6,
              height: day.hasData ? '100%' : '50%',
              backgroundColor: getUptimeColor(day.uptimePercentage, day.hasData, primaryColor),
              borderRadius: 0.25,
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

/**
 * StatusPageMonitorRow component
 * Displays a single monitor row with status indicator and uptime bar
 */
export function StatusPageMonitorRow({
  monitor,
  days = 30,
  primaryColor,
  showDetails = true,
}: StatusPageMonitorRowProps) {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const config = statusConfig[monitor.status];

  // Use provided history or generate mock data
  const history = monitor.uptimeHistory || generateMockHistory(days, monitor.uptimePercentage);

  // Format last checked time
  const lastCheckedText = monitor.lastCheckedAt
    ? `Last checked: ${new Date(monitor.lastCheckedAt).toLocaleTimeString()}`
    : 'Not checked yet';

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Main row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          cursor: showDetails ? 'pointer' : 'default',
          '&:hover': showDetails
            ? {
                backgroundColor: 'action.hover',
              }
            : {},
        }}
        onClick={() => showDetails && setExpanded(!expanded)}
      >
        {/* Status indicator */}
        <Tooltip title={config.label}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: config.color,
              flexShrink: 0,
            }}
          />
        </Tooltip>

        {/* Monitor name */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {monitor.displayName}
        </Typography>

        {/* Uptime bar (hidden on small mobile) */}
        {!isMobile && (
          <Box sx={{ width: 200, flexShrink: 0 }}>
            <MiniUptimeBar history={history} days={days} primaryColor={primaryColor} />
          </Box>
        )}

        {/* Uptime percentage */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: monitor.uptimePercentage >= 99 ? 'success.main' : 'text.secondary',
            minWidth: 60,
            textAlign: 'right',
          }}
        >
          {monitor.uptimePercentage.toFixed(2)}%
        </Typography>

        {/* Expand button */}
        {showDetails && (
          <IconButton size="small" sx={{ ml: 1 }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      {/* Expanded details */}
      {showDetails && (
        <Collapse in={expanded}>
          <Box
            sx={{
              px: 2,
              pb: 2,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'action.hover',
            }}
          >
            {/* Mobile uptime bar */}
            {isMobile && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  {days}-day uptime history
                </Typography>
                <MiniUptimeBar history={history} days={days} primaryColor={primaryColor} />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {/* Status */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: config.color }}>
                  {config.icon}
                  <Typography variant="body2" sx={{ fontWeight: 600, color: config.color }}>
                    {config.label}
                  </Typography>
                </Box>
              </Box>

              {/* Response time */}
              {monitor.avgResponseTime !== null && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Avg Response Time
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {monitor.avgResponseTime.toFixed(0)} ms
                  </Typography>
                </Box>
              )}

              {/* Uptime */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Uptime ({days} days)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {monitor.uptimePercentage.toFixed(2)}%
                </Typography>
              </Box>

              {/* Last checked */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Last Check
                </Typography>
                <Typography variant="body2">{lastCheckedText}</Typography>
              </Box>
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export default StatusPageMonitorRow;
